/**
 * LiveChatBanner.jsx — Pinned banner at the top of the Live chat page.
 * 1:1 port of the mobile version: pinned 16:9 banner, tap-to-upload, remove ×,
 * and a random car drive-by that drops a gift when carDriveKey increments.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const CAR_DEFS = [
  { key: "johnwick", aspectRatio: 606 / 450, scale: 1.0 },
  { key: "tate",     aspectRatio: 580 / 514, scale: 1.0 },
];

export default function LiveChatBanner({
  defaultBannerSrc,
  customBannerSrc,
  carSrcs,           // { johnwick, tate }
  carDriveKey = 0,
  caption = "",
  onPickBanner,
  onRemoveBanner,
}) {
  const fileInputRef = useRef(null);
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [busy, setBusy] = useState(false);
  const hasCustom = !!customBannerSrc;
  const bannerSrc = hasCustom ? customBannerSrc : defaultBannerSrc;

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setSize({ w: cr.width, h: cr.height });
    });
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const onBannerClick = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onPickBanner) return;
    setBusy(true);
    try {
      await onPickBanner(file);
    } finally {
      setBusy(false);
    }
  };
  const onRemoveClick = async (e) => {
    e.stopPropagation();
    if (!onRemoveBanner) return;
    if (!window.confirm("Remove your banner? The default picture will come back."))
      return;
    setBusy(true);
    try {
      await onRemoveBanner();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.wrap} data-testid="live-chat-banner">
      <style>{sheet}</style>
      <div
        ref={wrapRef}
        onClick={onBannerClick}
        style={styles.banner}
        role="button"
        tabIndex={0}
        data-testid="live-chat-banner-tap"
      >
        <img src={bannerSrc} alt="" draggable={false} style={styles.image} />
        {busy ? (
          <div style={styles.spinnerLayer}>
            <div className="lcb-spinner" />
          </div>
        ) : null}
      </div>
      {size.w > 0 ? (
        <CarOverlay
          driveKey={carDriveKey}
          bannerWidth={size.w}
          bannerHeight={size.h}
          carSrcs={carSrcs}
          caption={caption}
        />
      ) : null}
      {hasCustom && !busy ? (
        <button onClick={onRemoveClick} style={styles.removeBtn} aria-label="Remove custom banner" data-testid="live-chat-banner-remove">
          ×
        </button>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
        style={{ display: "none" }}
        onChange={onFileChange}
        data-testid="live-chat-banner-input"
      />
    </div>
  );
}

/* ---------------- Car + gift drop ---------------- */
function CarOverlay({ driveKey, bannerWidth, bannerHeight, carSrcs, caption }) {
  const [activeCar, setActiveCar] = useState(null);
  const [showCaption, setShowCaption] = useState(false);
  const [bags, setBags] = useState([]);
  const carRef = useRef(null);
  const lastKeyRef = useRef(0);
  const dropTimersRef = useRef([]);
  const clearTimers = () => {
    dropTimersRef.current.forEach((t) => clearTimeout(t));
    dropTimersRef.current = [];
  };
  const baseWidth = Math.max(1, Math.round(bannerWidth * 0.28));

  useEffect(() => {
    if (driveKey <= 0) return;
    if (driveKey === lastKeyRef.current) return;
    lastKeyRef.current = driveKey;
    clearTimers();
    const pickDef = CAR_DEFS[Math.floor(Math.random() * CAR_DEFS.length)];
    const pick = { ...pickDef, src: carSrcs[pickDef.key] };
    if (!pick.src) return;
    const carWidth  = Math.max(1, Math.round(baseWidth * (pick.scale ?? 1)));
    const carHeight = Math.max(1, Math.round(carWidth / pick.aspectRatio));
    const carTop    = Math.round(bannerHeight - carHeight - bannerHeight * 0.24);
    const duration = 3400;
    const startX = -carWidth - 20;
    const endX   = bannerWidth + 20;
    setActiveCar({ ...pick, carWidth, carHeight, carTop, startX, endX, duration });
    setShowCaption(!!caption);
    requestAnimationFrame(() => {
      const el = carRef.current;
      if (!el) return;
      el.style.transition = "none";
      el.style.transform = `translate3d(${startX}px, 0, 0)`;
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      requestAnimationFrame(() => {
        el.style.transition = `transform ${duration}ms linear`;
        el.style.transform = `translate3d(${endX}px, 0, 0)`;
      });
    });
    const doneT = setTimeout(() => { setActiveCar(null); setShowCaption(false); }, duration + 50);
    dropTimersRef.current.push(doneT);
    const dropPct = 0.5;
    const dropT = setTimeout(() => {
      const carLeftAtT = startX + (endX - startX) * dropPct;
      const bagX = carLeftAtT + carWidth * 0.5;
      const bagY = carTop - 6;
      const id = Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 6);
      setBags((prev) => [...prev, { id, x: bagX, y: bagY }]);
    }, duration * dropPct);
    dropTimersRef.current.push(dropT);
    return () => clearTimers();
  }, [driveKey, bannerWidth, bannerHeight, baseWidth, carSrcs]);

  const removeBag = useCallback((id) => {
    setBags((prev) => prev.filter((b) => b.id !== id));
  }, []);

  return (
    <>
      {showCaption && caption ? (
        <div style={{
          position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
          maxWidth: "90%", padding: "6px 14px", borderRadius: 999, zIndex: 6,
          background: "rgba(11,18,32,0.85)", color: "#fff", fontWeight: 900, fontSize: 13,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          boxShadow: "0 4px 14px rgba(0,0,0,0.35)", backdropFilter: "blur(6px)",
        }} data-testid="car-caption">
          💸 {caption}
        </div>
      ) : null}
      {activeCar ? (
        <div
          ref={carRef}
          style={{
            position: "absolute",
            pointerEvents: "none",
            top: activeCar.carTop,
            left: 0,
            width: activeCar.carWidth,
            height: activeCar.carHeight,
            transform: `translate3d(${activeCar.startX}px, 0, 0)`,
            willChange: "transform",
            zIndex: 3,
          }}
        >
          <img
            src={activeCar.src}
            alt=""
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: "contain", userSelect: "none" }}
          />
        </div>
      ) : null}
      {bags.map((b) => (
        <GiftBag
          key={b.id}
          x={b.x}
          y={b.y}
          fallDistance={Math.max(24, bannerHeight * 0.22)}
          onDone={() => removeBag(b.id)}
        />
      ))}
    </>
  );
}

function GiftBag({ x, y, fallDistance, onDone }) {
  const elRef = useRef(null);
  const tilt = useMemo(() => Math.round(Math.random() * 30 - 15), []);
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth;
    requestAnimationFrame(() => {
      el.style.transition =
        "transform 1400ms cubic-bezier(.45,.05,.55,.95), opacity 1600ms ease-in";
      el.style.transform = `translate3d(0, ${fallDistance}px, 0) rotate(${tilt}deg)`;
      el.style.opacity = "0";
    });
    const t = setTimeout(onDone, 1650);
    return () => clearTimeout(t);
  }, [fallDistance, tilt, onDone]);
  return (
    <div
      ref={elRef}
      style={{
        position: "absolute",
        left: x - 20,
        top: y - 20,
        width: 40,
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 36,
        lineHeight: "40px",
        pointerEvents: "none",
        transform: "translate3d(0, 0, 0) rotate(0deg)",
        opacity: 1,
        willChange: "transform, opacity",
        zIndex: 4,
      }}
    >
      🎁
    </div>
  );
}

/* ---------------- Styles ---------------- */
const styles = {
  wrap: { position: "relative", margin: "8px 16px 4px" },
  banner: {
    width: "100%",
    aspectRatio: "16 / 9",
    borderRadius: 14,
    overflow: "hidden",
    background: "#1A1A2A",
    cursor: "pointer",
    position: "relative",
  },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block", userSelect: "none" },
  removeBtn: {
    position: "absolute",
    top: 8, right: 8, width: 28, height: 28, borderRadius: 14,
    background: "rgba(0,0,0,0.6)", color: "#fff", border: "none",
    fontSize: 18, lineHeight: "24px", fontWeight: 900, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5,
  },
  spinnerLayer: {
    position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
};
const sheet = `
  .lcb-spinner {
    width: 28px; height: 28px;
    border: 3px solid rgba(255,255,255,0.2);
    border-top-color: #fff;
    border-radius: 50%;
    animation: lcb-spin 0.8s linear infinite;
  }
  @keyframes lcb-spin { to { transform: rotate(360deg); } }
`;
